import { RefObject, useEffect, useMemo, useRef } from "react";
import { StopBlock, TripperData } from "../types";
import {
  useTripperScrollContext,
  useTripperWidgetContext,
} from "../context/TripperWidgetContext";
import {
  businessTypesToKey,
  getBusinessTypesInItinerary,
  isShowingAllBusinessTypes,
  isStopVisibleForBusinessTypeFilter,
  selectionToKey,
} from "../utils/businessTypeFilters";
import { flattenStopBlocks } from "../utils/mapHelpers";

function findStopById(data: TripperData, itemId: string): StopBlock | null {
  for (const stop of flattenStopBlocks(data.segments)) {
    if (stop._id === itemId) return stop;
  }
  return null;
}

/** Clear scroll/map focus when the active stop is filtered out; reset on itinerary type changes. */
export function useBusinessTypeFilterSync({
  data,
  scrollContainerRef,
}: {
  data: TripperData;
  scrollContainerRef?: RefObject<HTMLElement | null>;
}) {
  const {
    activeItemId,
    setActiveItemId,
    setActiveStopGroupId,
    setIsMetadataInView,
  } = useTripperScrollContext();
  const {
    businessTypeFiltersEnabled,
    businessTypeFilterSelection,
    setBusinessTypeFilterSelection,
  } = useTripperWidgetContext();

  const filterSelectionKeyRef = useRef(selectionToKey(businessTypeFilterSelection));

  const itineraryTypesKey = useMemo(
    () => businessTypesToKey(getBusinessTypesInItinerary(data.segments, data.operatorByEntityId)),
    [data.segments, data.operatorByEntityId],
  );

  useEffect(() => {
    setBusinessTypeFilterSelection(null);
  }, [itineraryTypesKey, setBusinessTypeFilterSelection]);

  useEffect(() => {
    if (!businessTypeFiltersEnabled) return;

    const nextKey = selectionToKey(businessTypeFilterSelection);
    if (nextKey === filterSelectionKeyRef.current) return;
    filterSelectionKeyRef.current = nextKey;

    const container = scrollContainerRef?.current;
    if (container) {
      container.scrollTo({ top: 0, behavior: "instant" });
    }

    setActiveItemId(null);
    setActiveStopGroupId(null);
    setIsMetadataInView(true);
  }, [
    businessTypeFilterSelection,
    businessTypeFiltersEnabled,
    scrollContainerRef,
    setActiveItemId,
    setActiveStopGroupId,
    setIsMetadataInView,
  ]);

  useEffect(() => {
    if (
      !businessTypeFiltersEnabled
      || isShowingAllBusinessTypes(businessTypeFilterSelection)
      || !activeItemId
    ) {
      return;
    }

    const stop = findStopById(data, activeItemId);
    if (
      stop
      && !isStopVisibleForBusinessTypeFilter(
        stop,
        data.operatorByEntityId,
        businessTypeFilterSelection,
      )
    ) {
      setActiveItemId(null);
      setActiveStopGroupId(null);
    }
  }, [
    activeItemId,
    businessTypeFilterSelection,
    businessTypeFiltersEnabled,
    data,
    setActiveItemId,
    setActiveStopGroupId,
  ]);
}
